import { useState, useMemo } from "react";
import { PageHeader, Disclaimer } from "@/components/Shared";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Link } from "react-router-dom";

// Based on the Department of Home Affairs skilled visa points test (189/190/491)
const CATEGORIES = [
  {
    key: "age",
    title: "Age",
    description: "Points awarded at time of invitation.",
    options: [
      { v: 0, label: "Under 18 or 45+" },
      { v: 25, label: "18 – 24" },
      { v: 30, label: "25 – 32" },
      { v: 25, label: "33 – 39" },
      { v: 15, label: "40 – 44" },
    ],
  },
  {
    key: "english",
    title: "English language",
    description: "Higher proficiency = more points.",
    options: [
      { v: 0, label: "Competent (e.g. IELTS 6)" },
      { v: 10, label: "Proficient (e.g. IELTS 7)" },
      { v: 20, label: "Superior (e.g. IELTS 8+)" },
    ],
  },
  {
    key: "overseas_exp",
    title: "Overseas skilled employment",
    description: "Last 10 years.",
    options: [
      { v: 0, label: "Less than 3 years" },
      { v: 5, label: "3 – 4 years" },
      { v: 10, label: "5 – 7 years" },
      { v: 15, label: "8+ years" },
    ],
  },
  {
    key: "australian_exp",
    title: "Australian skilled employment",
    description: "Last 10 years.",
    options: [
      { v: 0, label: "Less than 1 year" },
      { v: 5, label: "1 – 2 years" },
      { v: 10, label: "3 – 4 years" },
      { v: 15, label: "5 – 7 years" },
      { v: 20, label: "8+ years" },
    ],
  },
  {
    key: "education",
    title: "Educational qualification",
    description: "Highest qualification.",
    options: [
      { v: 0, label: "Recognised qualification or AQF III/IV" },
      { v: 10, label: "Diploma or trade qualification" },
      { v: 15, label: "Bachelors / Masters" },
      { v: 20, label: "Doctorate" },
    ],
  },
  {
    key: "aus_study",
    title: "Australian study requirement",
    description: "At least 2 academic years in Australia.",
    options: [
      { v: 0, label: "Not met" },
      { v: 5, label: "Met" },
    ],
  },
  {
    key: "specialist_edu",
    title: "Specialist educational qualification",
    description: "STEM Masters/Doctorate at Australian institution.",
    options: [
      { v: 0, label: "No" },
      { v: 10, label: "Yes" },
    ],
  },
  {
    key: "community_language",
    title: "Credentialled community language",
    description: "NAATI accredited.",
    options: [
      { v: 0, label: "No" },
      { v: 5, label: "Yes" },
    ],
  },
  {
    key: "regional_study",
    title: "Study in regional Australia",
    description: "Lived & studied in a designated regional area.",
    options: [
      { v: 0, label: "No" },
      { v: 5, label: "Yes" },
    ],
  },
  {
    key: "partner",
    title: "Partner skills / English",
    description: "Single, or partner contribution.",
    options: [
      { v: 0, label: "Partner does not meet criteria" },
      { v: 5, label: "Partner has competent English" },
      { v: 10, label: "Partner is skilled (under 45 + skills assessment + competent English)" },
      { v: 10, label: "Single OR partner is Australian PR/citizen" },
    ],
  },
  {
    key: "professional_year",
    title: "Professional year in Australia",
    description: "Completed in last 4 years.",
    options: [
      { v: 0, label: "No" },
      { v: 5, label: "Yes" },
    ],
  },
  {
    key: "nomination",
    title: "State nomination / regional nomination",
    description: "Nomination uplift.",
    options: [
      { v: 0, label: "None (Subclass 189)" },
      { v: 5, label: "Subclass 190 — State nomination" },
      { v: 15, label: "Subclass 491 — Regional state / family sponsorship" },
    ],
  },
];

export default function PointsCalculator() {
  const [values, setValues] = useState(
    Object.fromEntries(CATEGORIES.map((c) => [c.key, "0"]))
  );

  const total = useMemo(
    () => Object.values(values).reduce((acc, v) => acc + parseInt(v || 0, 10), 0),
    [values]
  );

  const update = (k, v) => setValues((s) => ({ ...s, [k]: v }));
  const reset = () =>
    setValues(Object.fromEntries(CATEGORIES.map((c) => [c.key, "0"])));

  return (
    <div data-testid="points-calculator-page">
      <PageHeader
        eyebrow="Skilled Migration · 189 / 190 / 491"
        title="Points Test Calculator"
        subtitle="Based on the Department of Home Affairs skilled visa points table. The pass mark is 65 — most invited candidates score significantly higher."
        image="/assets/skilled.jpg"
      />

      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-10">
            {/* Inputs */}
            <div className="lg:col-span-8 space-y-7">
              {CATEGORIES.map((cat) => (
                <div key={cat.key} data-testid={`points-cat-${cat.key}`} className="border border-slate-200 p-6">
                  <div className="flex items-start justify-between gap-4 mb-4">
                    <div>
                      <h3 className="font-display text-xl text-[#29324A]">{cat.title}</h3>
                      <p className="text-xs text-slate-500 mt-1">{cat.description}</p>
                    </div>
                    <div className="text-right">
                      <div className="text-[10px] font-bold tracking-widest text-[#C43A89] uppercase">Selected</div>
                      <div className="font-display text-2xl text-[#29324A]">+{values[cat.key]}</div>
                    </div>
                  </div>

                  <Select value={String(values[cat.key])} onValueChange={(v) => update(cat.key, v)}>
                    <SelectTrigger data-testid={`points-select-${cat.key}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {cat.options.map((opt, i) => (
                        <SelectItem key={i} value={String(opt.v)}>
                          {opt.label} <span className="text-[#29324A] font-semibold ml-2">({opt.v} pts)</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ))}

              <Disclaimer>
                This calculator is a guide only. Final eligibility depends on current legislation, occupation list, skills assessment, invitation rounds and Department requirements. Please book a consultation for personalised advice.
              </Disclaimer>
            </div>

            {/* Sticky panel */}
            <aside className="lg:col-span-4">
              <div className="lg:sticky lg:top-24 space-y-4">
                <div className="bg-[#29324A] text-white p-7 relative overflow-hidden">
                  <div className="absolute inset-0 grain pointer-events-none" />
                  <div className="relative">
                    <div className="text-[10px] font-bold tracking-widest text-[#C43A89] uppercase mb-3">Your estimated total</div>
                    <div className="font-display text-7xl text-white leading-none tracking-tight tabular-nums" data-testid="points-total">
                      {total}
                    </div>
                    <div className="mt-4 text-sm text-slate-300">points</div>

                    <div className="mt-6 pt-5 border-t border-white/15 space-y-2 text-sm">
                      <Row label="Pass mark" value="65" />
                      <Row label="Typical invitation" value="80+" />
                      <Row
                        label="Status"
                        value={total >= 65 ? "Meets pass mark" : "Below pass mark"}
                        accent={total >= 65 ? "text-emerald-300" : "text-[#C43A89]"}
                      />
                    </div>

                    <div className="brand-rule my-6 opacity-50" />

                    <Link
                      to="/book-consultation"
                      data-testid="points-cta-book"
                      className="block bg-[#C43A89] hover:bg-[#a22b6e] text-white font-semibold py-3 text-center transition-colors btn-shimmer"
                    >
                      Discuss your score
                    </Link>
                    <Link
                      to="/pr-assessment"
                      className="block mt-2.5 border border-white/30 hover:bg-white/10 text-white font-semibold py-3 text-center transition-colors"
                    >
                      Free PR Assessment
                    </Link>
                    <button
                      data-testid="points-reset-btn"
                      onClick={reset}
                      className="block mt-2.5 w-full text-xs text-slate-400 hover:text-white py-2 transition-colors"
                    >
                      Reset all categories
                    </button>
                  </div>
                </div>

                <div className="bg-[#F8F9FA] border border-slate-200 p-5 text-xs text-slate-600 leading-relaxed">
                  <strong className="text-[#29324A]">Reference:</strong> Department of Home Affairs skilled visa points table for subclasses 189, 190 and 491. Selecting state nomination automatically uplifts eligible candidates.
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>
    </div>
  );
}

function Row({ label, value, accent }) {
  return (
    <div className="flex justify-between items-center">
      <span className="text-slate-300">{label}</span>
      <span className={`font-semibold ${accent || "text-white"}`}>{value}</span>
    </div>
  );
}
